import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Users, Search, Loader2, Shield, Ban, UserCheck, Key, Trash2,
  Eye, Edit, Coins, Send, Bell, ArrowLeft, RefreshCw, Activity,
  DollarSign, Target, CheckCircle, XCircle, AlertTriangle, FileText,
  TrendingUp, Clock, MoreVertical
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminCRM = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [systemHealth, setSystemHealth] = useState(null);
  const [adminLogs, setAdminLogs] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [activeTab, setActiveTab] = useState('users');
  
  // Modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [showBroadcastModal, setShowBroadcastModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  
  // Form states
  const [editForm, setEditForm] = useState({});
  const [tokenAmount, setTokenAmount] = useState('');
  const [tokenReason, setTokenReason] = useState('');
  const [broadcastTitle, setBroadcastTitle] = useState('');
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [processing, setProcessing] = useState(false);

  const headers = () => ({
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json'
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (searchQuery) {
      const filtered = users.filter(u => 
        u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.id?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers(users);
    }
  }, [searchQuery, users]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, healthRes, logsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/users`, { headers: headers() }),
        fetch(`${API_URL}/api/admin/system/health`, { headers: headers() }),
        fetch(`${API_URL}/api/admin/logs?limit=50`, { headers: headers() })
      ]);
      
      if (usersRes.ok) setUsers(await usersRes.json());
      if (healthRes.ok) setSystemHealth(await healthRes.json());
      if (logsRes.ok) setAdminLogs(await logsRes.json());
    } catch (e) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetail = async (userId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${userId}`, { headers: headers() });
      if (res.ok) {
        const data = await res.json();
        setUserDetail(data);
        setSelectedUser(data.user);
      }
    } catch (e) {
      toast.error('Failed to load user details');
    }
  };

  const handleSuspendUser = async (userId, suspend = true) => {
    setProcessing(true);
    try {
      const endpoint = suspend ? 'suspend' : 'unsuspend';
      const res = await fetch(`${API_URL}/api/admin/users/${userId}/${endpoint}`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ reason: 'Admin action' })
      });
      if (res.ok) {
        toast.success(suspend ? 'User suspended' : 'User unsuspended');
        fetchData();
        if (userDetail) fetchUserDetail(userId);
      } else {
        toast.error('Action failed');
      }
    } catch (e) {
      toast.error('Action failed');
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) return;
    setProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: headers()
      });
      if (res.ok) {
        toast.success('User deleted');
        setUserDetail(null);
        setSelectedUser(null);
        fetchData();
      }
    } catch (e) {
      toast.error('Delete failed');
    } finally {
      setProcessing(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    setProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${selectedUser.id}`, {
        method: 'PUT',
        headers: headers(),
        body: JSON.stringify(editForm)
      });
      if (res.ok) {
        toast.success('User updated');
        setShowEditModal(false);
        fetchData();
        fetchUserDetail(selectedUser.id);
      }
    } catch (e) {
      toast.error('Update failed');
    } finally {
      setProcessing(false);
    }
  };

  const handleAddTokens = async () => {
    if (!selectedUser || !tokenAmount) return;
    setProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${selectedUser.id}/add-tokens?amount=${tokenAmount}&reason=${encodeURIComponent(tokenReason)}`, {
        method: 'POST',
        headers: headers()
      });
      if (res.ok) {
        toast.success(`Added ${tokenAmount} KBTCOIN`);
        setShowTokenModal(false);
        setTokenAmount('');
        setTokenReason('');
        fetchUserDetail(selectedUser.id);
      }
    } catch (e) {
      toast.error('Failed to add tokens');
    } finally {
      setProcessing(false);
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser || !newPassword) return;
    setProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${selectedUser.id}/reset-password?new_password=${encodeURIComponent(newPassword)}`, {
        method: 'POST',
        headers: headers()
      });
      if (res.ok) {
        toast.success('Password reset successfully');
        setShowResetPasswordModal(false);
        setNewPassword('');
      }
    } catch (e) {
      toast.error('Password reset failed');
    } finally {
      setProcessing(false);
    }
  };

  const handleBroadcast = async () => {
    if (!broadcastTitle || !broadcastMessage) return;
    setProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/notifications/broadcast?title=${encodeURIComponent(broadcastTitle)}&message=${encodeURIComponent(broadcastMessage)}`, {
        method: 'POST',
        headers: headers()
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Notification sent to ${data.recipients} users`);
        setShowBroadcastModal(false);
        setBroadcastTitle('');
        setBroadcastMessage('');
      }
    } catch (e) {
      toast.error('Broadcast failed');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(220,25%,6%)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(220,25%,6%)] text-white">
      {/* Header */}
      <div className="border-b border-white/10 bg-[hsl(220,20%,7%)]">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/admin')} className="text-gray-400 hover:text-white">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
              </Button>
              <div>
                <h1 className="text-2xl font-bold">Admin CRM</h1>
                <p className="text-gray-400 text-sm">Full platform control & user management</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={fetchData} variant="outline" size="sm" className="border-white/20">
                <RefreshCw className="w-4 h-4 mr-2" /> Refresh
              </Button>
              <Button onClick={() => setShowBroadcastModal(true)} className="bg-cyan-500 hover:bg-cyan-600">
                <Bell className="w-4 h-4 mr-2" /> Broadcast
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* System Health Cards */}
        {systemHealth && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
            <Card className="bg-[hsl(220,20%,8%)] border-white/10">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Users className="w-8 h-8 text-blue-400" />
                  <div>
                    <p className="text-2xl font-bold">{systemHealth.users?.total || 0}</p>
                    <p className="text-xs text-gray-500">Total Users</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[hsl(220,20%,8%)] border-white/10">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Target className="w-8 h-8 text-green-400" />
                  <div>
                    <p className="text-2xl font-bold">{systemHealth.challenges?.active || 0}</p>
                    <p className="text-xs text-gray-500">Active Challenges</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[hsl(220,20%,8%)] border-white/10">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Shield className="w-8 h-8 text-cyan-400" />
                  <div>
                    <p className="text-2xl font-bold">{systemHealth.challenges?.funded || 0}</p>
                    <p className="text-xs text-gray-500">Funded Accounts</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[hsl(220,20%,8%)] border-white/10">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Coins className="w-8 h-8 text-yellow-400" />
                  <div>
                    <p className="text-2xl font-bold">{systemHealth.tokens?.in_circulation?.toLocaleString() || 0}</p>
                    <p className="text-xs text-gray-500">KBT Circulation</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[hsl(220,20%,8%)] border-white/10">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <FileText className="w-8 h-8 text-orange-400" />
                  <div>
                    <p className="text-2xl font-bold">{systemHealth.pending_actions?.kyc_reviews || 0}</p>
                    <p className="text-xs text-gray-500">Pending KYC</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[hsl(220,20%,8%)] border-white/10">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <DollarSign className="w-8 h-8 text-purple-400" />
                  <div>
                    <p className="text-2xl font-bold">{systemHealth.pending_actions?.withdrawals || 0}</p>
                    <p className="text-xs text-gray-500">Pending Withdrawals</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Users List */}
          <div className="lg:col-span-2">
            <Card className="bg-[hsl(220,20%,8%)] border-white/10">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>User Management</span>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <Input
                      placeholder="Search users..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 w-64 bg-white/5 border-white/10"
                    />
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-auto max-h-[600px]">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/10">
                        <TableHead>User</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Joined</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredUsers.map((user) => (
                        <TableRow 
                          key={user.id} 
                          className={`border-white/5 cursor-pointer hover:bg-white/5 ${selectedUser?.id === user.id ? 'bg-cyan-500/10' : ''}`}
                          onClick={() => fetchUserDetail(user.id)}
                        >
                          <TableCell>
                            <div>
                              <p className="font-medium">{user.full_name || user.name || 'N/A'}</p>
                              <p className="text-xs text-gray-500">{user.email}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            {user.suspended ? (
                              <Badge className="bg-red-500/20 text-red-400">Suspended</Badge>
                            ) : user.is_active ? (
                              <Badge className="bg-green-500/20 text-green-400">Active</Badge>
                            ) : (
                              <Badge className="bg-gray-500/20 text-gray-400">Inactive</Badge>
                            )}
                            {user.role === 'admin' && (
                              <Badge className="ml-1 bg-purple-500/20 text-purple-400">Admin</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-gray-400 text-sm">
                            {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                          </TableCell>
                          <TableCell>
                            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); fetchUserDetail(user.id); }}>
                              <Eye className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* User Detail Panel */}
          <div>
            {userDetail ? (
              <Card className="bg-[hsl(220,20%,8%)] border-white/10 sticky top-4">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>User Details</span>
                    <Button variant="ghost" size="sm" onClick={() => { setUserDetail(null); setSelectedUser(null); }}>
                      <XCircle className="w-4 h-4" />
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* User Info */}
                  <div className="space-y-2">
                    <p className="text-lg font-bold">{userDetail.user?.full_name || userDetail.user?.name}</p>
                    <p className="text-gray-400 text-sm">{userDetail.user?.email}</p>
                    <div className="flex gap-2">
                      {userDetail.user?.suspended ? (
                        <Badge className="bg-red-500/20 text-red-400">Suspended</Badge>
                      ) : (
                        <Badge className="bg-green-500/20 text-green-400">Active</Badge>
                      )}
                      {userDetail.user?.is_verified && (
                        <Badge className="bg-blue-500/20 text-blue-400">Verified</Badge>
                      )}
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-white/5 rounded-lg">
                      <p className="text-xs text-gray-500">Challenges</p>
                      <p className="text-lg font-bold">{userDetail.stats?.total_challenges || 0}</p>
                    </div>
                    <div className="p-3 bg-white/5 rounded-lg">
                      <p className="text-xs text-gray-500">Total Spent</p>
                      <p className="text-lg font-bold">${userDetail.stats?.total_spent?.toFixed(2) || '0.00'}</p>
                    </div>
                    <div className="p-3 bg-white/5 rounded-lg">
                      <p className="text-xs text-gray-500">KBT Balance</p>
                      <p className="text-lg font-bold text-yellow-400">{userDetail.stats?.token_balance || 0}</p>
                    </div>
                    <div className="p-3 bg-white/5 rounded-lg">
                      <p className="text-xs text-gray-500">KYC Status</p>
                      <p className="text-lg font-bold">{userDetail.kyc?.status || 'None'}</p>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="border-white/20"
                        onClick={() => { setEditForm(userDetail.user); setShowEditModal(true); }}
                      >
                        <Edit className="w-4 h-4 mr-1" /> Edit
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="border-yellow-500/30 text-yellow-400"
                        onClick={() => setShowTokenModal(true)}
                      >
                        <Coins className="w-4 h-4 mr-1" /> Add Tokens
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="border-white/20"
                        onClick={() => setShowResetPasswordModal(true)}
                      >
                        <Key className="w-4 h-4 mr-1" /> Reset Password
                      </Button>
                      {userDetail.user?.suspended ? (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="border-green-500/30 text-green-400"
                          onClick={() => handleSuspendUser(userDetail.user.id, false)}
                          disabled={processing}
                        >
                          <UserCheck className="w-4 h-4 mr-1" /> Unsuspend
                        </Button>
                      ) : (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="border-orange-500/30 text-orange-400"
                          onClick={() => handleSuspendUser(userDetail.user.id, true)}
                          disabled={processing}
                        >
                          <Ban className="w-4 h-4 mr-1" /> Suspend
                        </Button>
                      )}
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full border-red-500/30 text-red-400"
                      onClick={() => handleDeleteUser(userDetail.user.id)}
                      disabled={processing}
                    >
                      <Trash2 className="w-4 h-4 mr-1" /> Delete User
                    </Button>
                  </div>

                  {/* Recent Challenges */}
                  {userDetail.challenges?.length > 0 && (
                    <div>
                      <p className="text-sm font-medium mb-2">Recent Challenges</p>
                      <div className="space-y-2 max-h-40 overflow-auto">
                        {userDetail.challenges.slice(0, 5).map((c) => (
                          <div key={c.id} className="p-2 bg-white/5 rounded text-sm flex justify-between">
                            <span>{c.package_name}</span>
                            <Badge className={c.status === 'funded' ? 'bg-green-500/20 text-green-400' : c.status === 'failed' ? 'bg-red-500/20 text-red-400' : 'bg-blue-500/20 text-blue-400'}>
                              {c.status}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-[hsl(220,20%,8%)] border-white/10">
                <CardContent className="p-8 text-center">
                  <Users className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">Select a user to view details</p>
                </CardContent>
              </Card>
            )}

            {/* Admin Activity Log */}
            <Card className="bg-[hsl(220,20%,8%)] border-white/10 mt-4">
              <CardHeader>
                <CardTitle className="text-sm">Recent Admin Activity</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-60 overflow-auto">
                  {adminLogs.slice(0, 10).map((log) => (
                    <div key={log.id} className="p-2 bg-white/5 rounded text-xs">
                      <div className="flex justify-between">
                        <span className="text-cyan-400">{log.action}</span>
                        <span className="text-gray-500">{new Date(log.timestamp).toLocaleString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Edit User Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-[hsl(220,20%,10%)] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Full Name</Label>
              <Input 
                value={editForm.full_name || ''} 
                onChange={(e) => setEditForm({...editForm, full_name: e.target.value})}
                className="bg-white/5 border-white/10"
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input 
                value={editForm.phone || ''} 
                onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                className="bg-white/5 border-white/10"
              />
            </div>
            <div>
              <Label>Country</Label>
              <Input 
                value={editForm.country || ''} 
                onChange={(e) => setEditForm({...editForm, country: e.target.value})}
                className="bg-white/5 border-white/10"
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea 
                value={editForm.notes || ''} 
                onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                className="bg-white/5 border-white/10"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
            <Button onClick={handleUpdateUser} disabled={processing}>
              {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Tokens Modal */}
      <Dialog open={showTokenModal} onOpenChange={setShowTokenModal}>
        <DialogContent className="bg-[hsl(220,20%,10%)] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Add KBTCOIN Tokens</DialogTitle>
            <DialogDescription className="text-gray-400">
              Manually add tokens to {selectedUser?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Amount</Label>
              <Input 
                type="number"
                value={tokenAmount} 
                onChange={(e) => setTokenAmount(e.target.value)}
                placeholder="Enter amount"
                className="bg-white/5 border-white/10"
              />
            </div>
            <div>
              <Label>Reason</Label>
              <Input 
                value={tokenReason} 
                onChange={(e) => setTokenReason(e.target.value)}
                placeholder="e.g., Bonus, Correction, Promotion"
                className="bg-white/5 border-white/10"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTokenModal(false)}>Cancel</Button>
            <Button onClick={handleAddTokens} disabled={processing} className="bg-yellow-500 hover:bg-yellow-600 text-black">
              {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Tokens'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Modal */}
      <Dialog open={showResetPasswordModal} onOpenChange={setShowResetPasswordModal}>
        <DialogContent className="bg-[hsl(220,20%,10%)] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription className="text-gray-400">
              Set a new password for {selectedUser?.email}
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>New Password</Label>
            <Input 
              type="password"
              value={newPassword} 
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              className="bg-white/5 border-white/10"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResetPasswordModal(false)}>Cancel</Button>
            <Button onClick={handleResetPassword} disabled={processing}>
              {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reset Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Broadcast Modal */}
      <Dialog open={showBroadcastModal} onOpenChange={setShowBroadcastModal}>
        <DialogContent className="bg-[hsl(220,20%,10%)] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Broadcast Notification</DialogTitle>
            <DialogDescription className="text-gray-400">
              Send a notification to all active users
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title</Label>
              <Input 
                value={broadcastTitle} 
                onChange={(e) => setBroadcastTitle(e.target.value)}
                placeholder="Notification title"
                className="bg-white/5 border-white/10"
              />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea 
                value={broadcastMessage} 
                onChange={(e) => setBroadcastMessage(e.target.value)}
                placeholder="Notification message"
                className="bg-white/5 border-white/10"
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBroadcastModal(false)}>Cancel</Button>
            <Button onClick={handleBroadcast} disabled={processing} className="bg-cyan-500 hover:bg-cyan-600">
              {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send to All Users'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCRM;
